import type { Responsive } from '#types/common'
import type { ComponentPercentHeight } from '#types/content'

const ANCHOR_TO_OBJECT_POSITION: Record<string, string> = {
  'top-left': '0% 0%',
  'top-center': '50% 0%',
  'top-right': '100% 0%',
  'middle-left': '0% 50%',
  'middle-center': '50% 50%',
  'middle-right': '100% 50%',
  'bottom-left': '0% 100%',
  'bottom-center': '50% 100%',
  'bottom-right': '100% 100%',
}

/**
 * Whether a CMS percent-height value is set for a breakpoint (non-empty string).
 * @category Utils
 */
export const hasPercentHeightAtBreakpoint = (value?: string) =>
  value !== undefined && value !== ''

/** CSS custom properties for viewport-relative component height (shared by CMS banners / collection slides). */
export const getPercentHeightCssVars = (
  cph: ComponentPercentHeight | undefined,
  headerOffsetPx: number,
): Record<string, string> => {
  const vars: Record<string, string> = {}
  if (hasPercentHeightAtBreakpoint(cph?.small)) {
    vars['--h'] = `calc((100svh - ${headerOffsetPx}px) * ${Number(cph!.small) / 100})`
  }
  if (hasPercentHeightAtBreakpoint(cph?.large)) {
    vars['--h-lg'] = `calc((100svh - ${headerOffsetPx}px) * ${Number(cph!.large) / 100})`
  }
  return vars
}

/**
 * Converts a CMS anchor string (`'top-left'`, `'middle-center'`, …) to its CSS `object-position`
 * equivalent (`'0% 0%'`, `'50% 50%'`, …). Returns `undefined` for falsy input and falls back to
 * centered (`'50% 50%'`) when the anchor is unrecognised.
 * @param anchor - The anchor name from the CMS payload.
 * @returns The matching `object-position` value, or `undefined`.
 * @category Utils
 */
export const toObjectPosition = (anchor?: string) => {
  if (!anchor) return undefined
  const key = String(anchor).trim().toLowerCase()
  return ANCHOR_TO_OBJECT_POSITION[key] ?? '50% 50%'
}

/**
 * Builds a responsive `object-position` map. Missing anchors
 * fall back to centered crop (preserves pre-anchor behaviour).
 * @param anchorSmall - The anchor for the small breakpoint.
 * @param anchorLarge - The anchor for the large breakpoint.
 * @returns Partial responsive map of `object-position` values, or `undefined` when neither
 *          breakpoint contributes a value.
 * @category Utils
 */
export const getResponsiveObjectPosition = (anchorSmall?: string, anchorLarge?: string) => {
  const small = anchorSmall ? toObjectPosition(anchorSmall) : undefined
  const large = anchorLarge ? toObjectPosition(anchorLarge) : undefined
  if (!small && !large) return undefined
  return {
    ...(small && { sm: small, md: small }),
    ...(large && { lg: large }),
  } as Partial<Responsive<string>>
}
